// yard/YardEnvironment.jsx - Procedural yard with grass, fence, trees
import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Procedural grass ground
const Ground = ({ darkMode }) => {
  const meshRef = useRef();

  // Create grass texture pattern
  const grassTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    // Base grass color
    ctx.fillStyle = darkMode ? '#1a3328' : '#4a7c3f';
    ctx.fillRect(0, 0, 512, 512);

    // Add grass blades variation
    for (let i = 0; i < 5000; i++) {
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const brightness = Math.random() * 30 - 15;
      const r = darkMode ? 26 + brightness : 74 + brightness;
      const g = darkMode ? 51 + brightness : 124 + brightness;
      const b = darkMode ? 40 + brightness : 63 + brightness;
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fillRect(x, y, 2, 2);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(8, 8);
    return texture;
  }, [darkMode]);

  return (
    <mesh
      ref={meshRef}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.01, 0]}
      receiveShadow
    >
      <planeGeometry args={[80, 60]} />
      <meshStandardMaterial
        map={grassTexture}
        roughness={0.9}
        metalness={0}
      />
    </mesh>
  );
};

// Fence posts and rails
const Fence = ({ darkMode }) => {
  const posts = useMemo(() => {
    const positions = [];
    // Front fence
    for (let x = -38; x <= 38; x += 4) {
      positions.push({ x, z: 28, rotation: 0 });
    }
    // Back fence
    for (let x = -38; x <= 38; x += 4) {
      positions.push({ x, z: -28, rotation: 0 });
    }
    // Left fence
    for (let z = -28; z <= 28; z += 4) {
      positions.push({ x: -38, z, rotation: Math.PI / 2 });
    }
    // Right fence
    for (let z = -28; z <= 28; z += 4) {
      positions.push({ x: 38, z, rotation: Math.PI / 2 });
    }
    return positions;
  }, []);

  const woodColor = darkMode ? '#2d1f1a' : '#8B4513';

  return (
    <group>
      {/* Fence posts */}
      {posts.map((post, i) => (
        <mesh
          key={`post-${i}`}
          position={[post.x, 1.2, post.z]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[0.3, 2.4, 0.3]} />
          <meshStandardMaterial color={woodColor} roughness={0.8} />
        </mesh>
      ))}

      {/* Horizontal rails - front */}
      <mesh position={[0, 0.6, 28]} castShadow>
        <boxGeometry args={[76, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      <mesh position={[0, 1.4, 28]} castShadow>
        <boxGeometry args={[76, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      <mesh position={[0, 2.0, 28]} castShadow>
        <boxGeometry args={[76, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>

      {/* Back fence rails */}
      <mesh position={[0, 0.6, -28]} castShadow>
        <boxGeometry args={[76, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      <mesh position={[0, 1.4, -28]} castShadow>
        <boxGeometry args={[76, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      <mesh position={[0, 2.0, -28]} castShadow>
        <boxGeometry args={[76, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>

      {/* Side fence rails */}
      <mesh position={[-38, 0.6, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
        <boxGeometry args={[56, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      <mesh position={[-38, 1.4, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
        <boxGeometry args={[56, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      <mesh position={[-38, 2.0, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
        <boxGeometry args={[56, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>

      <mesh position={[38, 0.6, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
        <boxGeometry args={[56, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      <mesh position={[38, 1.4, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
        <boxGeometry args={[56, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      <mesh position={[38, 2.0, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
        <boxGeometry args={[56, 0.15, 0.15]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
    </group>
  );
};

// Simple low-poly tree
const Tree = ({ position, scale = 1, darkMode }) => {
  const trunkColor = darkMode ? '#2d1f1a' : '#654321';
  const leavesColor = darkMode ? '#1a4d2e' : '#228B22';

  return (
    <group position={position} scale={scale}>
      {/* Trunk */}
      <mesh position={[0, 1.5, 0]} castShadow>
        <cylinderGeometry args={[0.3, 0.5, 3, 8]} />
        <meshStandardMaterial color={trunkColor} roughness={0.9} />
      </mesh>
      {/* Foliage layers */}
      <mesh position={[0, 4, 0]} castShadow>
        <coneGeometry args={[2, 3, 8]} />
        <meshStandardMaterial color={leavesColor} roughness={0.8} />
      </mesh>
      <mesh position={[0, 5.5, 0]} castShadow>
        <coneGeometry args={[1.5, 2.5, 8]} />
        <meshStandardMaterial color={leavesColor} roughness={0.8} />
      </mesh>
      <mesh position={[0, 6.8, 0]} castShadow>
        <coneGeometry args={[1, 2, 8]} />
        <meshStandardMaterial color={leavesColor} roughness={0.8} />
      </mesh>
    </group>
  );
};

// Dog house
const DogHouse = ({ position, darkMode }) => {
  const woodColor = darkMode ? '#3d2a1f' : '#8B4513';
  const roofColor = darkMode ? '#1a1a2e' : '#4a4a4a';

  return (
    <group position={position}>
      {/* Base */}
      <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
        <boxGeometry args={[3, 1.5, 2.5]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      {/* Entrance hole */}
      <mesh position={[0, 0.5, 1.26]} castShadow>
        <cylinderGeometry args={[0.5, 0.5, 0.1, 16]} rotation={[Math.PI / 2, 0, 0]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Roof */}
      <mesh position={[0, 1.8, 0]} rotation={[0, 0, 0]} castShadow>
        <boxGeometry args={[3.4, 0.2, 3]} />
        <meshStandardMaterial color={roofColor} roughness={0.7} />
      </mesh>
      <mesh position={[0, 2.3, 0]} castShadow>
        <coneGeometry args={[2.2, 1, 4]} rotation={[0, Math.PI / 4, 0]} />
        <meshStandardMaterial color={roofColor} roughness={0.7} />
      </mesh>
    </group>
  );
};

// Water bowl
const WaterBowl = ({ position }) => {
  return (
    <group position={position}>
      <mesh position={[0, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.5, 0.4, 0.3, 16]} />
        <meshStandardMaterial color="#808080" roughness={0.3} metalness={0.6} />
      </mesh>
      {/* Water */}
      <mesh position={[0, 0.2, 0]}>
        <cylinderGeometry args={[0.4, 0.4, 0.1, 16]} />
        <meshStandardMaterial color="#4a90c2" roughness={0.1} metalness={0.2} transparent opacity={0.8} />
      </mesh>
    </group>
  );
};

// Pipeline stage markers
const StageMarkers = ({ darkMode }) => {
  const stages = [
    { name: 'Prospect', x: -20 },
    { name: 'Qualified', x: -12 },
    { name: 'Proposal', x: -4 },
    { name: 'Negotiation', x: 4 },
    { name: 'Closing', x: 12 },
    { name: 'Won', x: 20 }
  ];

  return (
    <group position={[0, 0.01, 25]}>
      {stages.map((stage, i) => (
        <group key={stage.name} position={[stage.x, 0, 0]}>
          {/* Marker post */}
          <mesh position={[0, 0.5, 0]} castShadow>
            <cylinderGeometry args={[0.15, 0.15, 1, 8]} />
            <meshStandardMaterial
              color={darkMode ? '#4a5568' : '#718096'}
              roughness={0.5}
            />
          </mesh>
          {/* Sign board */}
          <mesh position={[0, 1.2, 0]} castShadow>
            <boxGeometry args={[2.5, 0.6, 0.1]} />
            <meshStandardMaterial
              color={darkMode ? '#1e293b' : '#f8fafc'}
              roughness={0.3}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
};

// Main environment component
const YardEnvironment = ({ darkMode }) => {
  // Tree positions around the yard
  const trees = useMemo(() => [
    { position: [-35, 0, -25], scale: 1.2 },
    { position: [-30, 0, 25], scale: 1.0 },
    { position: [35, 0, -20], scale: 1.3 },
    { position: [32, 0, 22], scale: 0.9 },
    { position: [-33, 0, 0], scale: 1.1 },
    { position: [36, 0, 5], scale: 1.0 },
    { position: [-25, 0, -26], scale: 0.8 },
    { position: [25, 0, -26], scale: 1.0 },
  ], []);

  return (
    <group>
      <Ground darkMode={darkMode} />
      <Fence darkMode={darkMode} />

      {/* Trees */}
      {trees.map((tree, i) => (
        <Tree
          key={`tree-${i}`}
          position={tree.position}
          scale={tree.scale}
          darkMode={darkMode}
        />
      ))}

      {/* Dog houses */}
      <DogHouse position={[-30, 0, -20]} darkMode={darkMode} />
      <DogHouse position={[30, 0, -20]} darkMode={darkMode} />

      {/* Water bowls */}
      <WaterBowl position={[-28, 0, -18]} />
      <WaterBowl position={[28, 0, -18]} />
      <WaterBowl position={[0, 0, 20]} />

      {/* Stage markers */}
      <StageMarkers darkMode={darkMode} />
    </group>
  );
};

export default YardEnvironment;
